<template>
    <div v-if="show" class="fullscreen-preview" @click="handleBackdropClick">
        <!-- 视频容器 -->
        <div class="video-container" @click.stop>
            <!-- 封面图（加载中显示） -->
            <img v-if="isLoading" :src="poster" class="poster-image" />

            <!-- 视频播放器 -->
            <video playsinline webkit-playsinline x5-playsinline x5-video-player-type="h5"
                x5-video-player-fullscreen="false" preload="auto" muted ref="videoRef" :src="currentVideo"
                :poster="poster" class="preview-video" @loadedmetadata="handleLoadedMetadata" @ended="handleEnded"
                @error="handleVideoError" @click="togglePlay"></video>

            <!-- 播放/暂停按钮 -->
            <div v-if="!isPlaying && !isLoading" class="play-overlay" @click="togglePlay">
                <img src="@/assets/images/chat/chatRecord/paly.svg" class="play-icon" alt="play">
            </div>

            <!-- 加载中 -->
            <div v-if="isLoading" class="loading-overlay">
                <div class="loading-spinner"></div>
            </div>

            <!-- 错误状态 -->
            <div v-if="hasError" class="error-overlay">
                <van-icon name="video-fail" size="32" color="#fff" />
                <span class="error-text">Video failed to load</span>
            </div>

            <!-- 进度条 -->

            <div class="progress-bar-container" @click="handleSeek" v-if="isPlaying && duration > 0">
                <div class="time-display">
                    <div>{{ formatTime(currentTime) }}</div>
                    <div>
                        {{ formatTime(duration) }}
                    </div>
                </div>
                <div class="progress-bar">
                    <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
                </div>
            </div>
        </div>

        <!-- 顶部控制栏 -->
        <div class="top-controls">
            <div class="close-btn" @click="handleClose">
                <img src="@/assets/images/message/close_1.svg" class="w-28px h-28px" alt="">
            </div>
        </div>
        <div class="top-controls-r">
            <div class="close-btn" @click="handleDownload">
                <img src="@/assets/images/message/download.svg" class="w-28px h-28px" alt="">
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { showLoadingToast, showSuccessToast, showErrorToast } from '@/utils/toast'
import bridge from '@/utils/bridge'
import { useI18n } from 'vue-i18n'

interface Props {
    show: boolean
    videos: string[]
    poster?: string
    startPosition?: number
}

interface Emits {
    (e: 'update:show', value: boolean): void
    (e: 'close'): void
    (e: 'load'): void
    (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
    startPosition: 0,
    poster: ''
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

// Refs
const videoRef = ref<HTMLVideoElement | null>(null)

// State
const currentIndex = ref(props.startPosition)
const isLoading = ref(false)
const hasError = ref(false)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)
let rafId: number | null = null

// Computed
const currentVideo = computed(() => props.videos[currentIndex.value] || '')
const poster = computed(() => props.poster || '')

const progressPercent = computed(() => {
    if (duration.value <= 0) return 0
    return (currentTime.value / duration.value) * 100
})

// 立即尝试自动播放
const tryAutoPlay = () => {
    if (!videoRef.value) return
    
    const video = videoRef.value
    // 确保视频已加载
    if (video.readyState >= 3) { // HAVE_FUTURE_DATA
        video.play().then(() => {
            isPlaying.value = true
            isLoading.value = false
            startRafLoop()
        }).catch(() => {
            // 自动播放失败，等待用户点击
            isLoading.value = false
        })
    } else {
        // 视频未加载完成，等待 canplay 事件
        const handleCanPlay = () => {
            video.play().then(() => {
                isPlaying.value = true
                isLoading.value = false
                startRafLoop()
            }).catch(() => {
                isLoading.value = false
            })
            video.removeEventListener('canplay', handleCanPlay)
        }
        video.addEventListener('canplay', handleCanPlay)
    }
}

// 监听显示状态
watch(() => props.show, (newVal) => {
    if (newVal) {
        isLoading.value = true
        hasError.value = false
        isPlaying.value = false
        currentTime.value = 0
        duration.value = 0
        document.body.style.overflow = 'hidden'
        // 立即尝试播放，不等待元数据
        nextTick(() => {
            tryAutoPlay()
        })
    } else {
        // 关闭时暂停视频
        if (videoRef.value) {
            videoRef.value.pause()
        }
        isPlaying.value = false
        document.body.style.overflow = ''
    }
})

// 监听视频切换
watch(() => props.videos, () => {
    if (props.videos.length > 0) {
        currentIndex.value = Math.min(props.startPosition, props.videos.length - 1)
        isLoading.value = true
        hasError.value = false
    }
}, { immediate: true })

// 视频元数据加载完成
const handleLoadedMetadata = () => {
    isLoading.value = false
    hasError.value = false
    if (videoRef.value) {
        duration.value = videoRef.value.duration
    }
    emit('load')
}

// 播放结束
const handleEnded = () => {
    isPlaying.value = false
    stopRafLoop()
    if (videoRef.value) {
        videoRef.value.currentTime = 0
    }
}

// 视频错误
const handleVideoError = () => {
    isLoading.value = false
    hasError.value = true
    isPlaying.value = false
    emit('error')
}

// 切换播放/暂停
const togglePlay = () => {
    if (!videoRef.value) return

    if (isPlaying.value) {
        videoRef.value.pause()
        isPlaying.value = false
        stopRafLoop()
    } else {
        if (hasError.value) {
            hasError.value = false
            isLoading.value = true
        }
        videoRef.value.play().then(() => {
            isPlaying.value = true
            startRafLoop()
        }).catch(() => {
            hasError.value = true
            isLoading.value = false
        })
    }
}

// RAF 循环同步进度
const startRafLoop = () => {
    const update = () => {
        if (videoRef.value && isPlaying.value) {
            currentTime.value = videoRef.value.currentTime
            rafId = requestAnimationFrame(update)
        }
    }
    rafId = requestAnimationFrame(update)
}

const stopRafLoop = () => {
    if (rafId !== null) {
        cancelAnimationFrame(rafId)
        rafId = null
    }
}

// 跳转
const handleSeek = (e: MouseEvent) => {
    if (!videoRef.value || duration.value <= 0) return

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    videoRef.value.currentTime = percent * duration.value
}

// 格式化时间
const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

// 关闭
const handleClose = () => {
    stopRafLoop()
    if (videoRef.value) {
        videoRef.value.pause()
    }
    emit('update:show', false)
    emit('close')
}

// 点击背景关闭
const handleBackdropClick = (e: Event) => {
    if (e.target === e.currentTarget) {
        handleClose()
    }
}

// 下载视频
const handleDownload = async () => {
    showLoadingToast(t('downloading...'))
    await bridge.callApp('photoSave', {
        image: currentVideo.value
    }).then((res: any) => {
        if (res) {
            showSuccessToast(t('downloadSuccess'))
        } else {
            showErrorToast(t('downloadFailed'))
        }
    }).catch(() => {
        showErrorToast(t('downloadFailed'))
    }).finally(() => {
        // closeToast()
    })
}

// 键盘事件
const handleKeydown = (e: KeyboardEvent) => {
    if (!props.show) return

    switch (e.key) {
        case 'Escape':
            handleClose()
            break
        case ' ':
            e.preventDefault()
            togglePlay()
            break
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    document.body.style.overflow = ''
})
</script>

<style scoped lang="scss">
.fullscreen-preview {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
}

.video-container {
    position: relative;
    max-width: 100vw;
    height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
}

.preview-video {
    width: 100vw;
    height: 100vh;
    object-fit: cover;
    object-position: center;
}

.poster-image {
    position: absolute;
    width: 100%;
    height: 100%;
    object-fit: cover;
    // object-position: center;
}

.play-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80px;
    height: 80px;
    // background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
}

.play-icon {
    width: 64px;
    height: 64px;
}

.loading-overlay,
.error-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    color: #fff;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.error-text {
    font-size: 14px;
    font-weight: 500;
}

.progress-bar-container {
    position: absolute;
    bottom: 0%;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    z-index: 100;
    padding: 8px 16px;
    // background: rgba(0, 0, 0, 0.6);
    // border-radius: 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
}

.progress-bar {
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
    cursor: pointer;
    overflow: visible;
    position: relative;
}

.progress-filled {
    height: 100%;
    background: #fff;
    border-radius: 2px;
    transition: width 50ms linear;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        right: -6px;
        top: 50%;
        transform: translateY(-50%);
        width: 8px;
        height: 8px;
        background: #fff;
        border-radius: 50%;
        box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
    }
}

.time-display {
    text-align: center;
    color: #fff;
    font-size: 12px;
    display: flex;
    justify-content: space-between;
}

.top-controls {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 999;
}

.top-controls-r {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 999;
}

.close-btn {
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
}

.bottom-controls {
    position: absolute;
    bottom: 12%;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    z-index: 10;
    width: 90%;
}

.download-btn {
    width: 40%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: linear-gradient(180deg, #5A68FF 0%, #95A5FF 100%);
    color: #fff;
    padding: 12px 24px;
    border-radius: 25px;
    cursor: pointer;
    font-weight: 900;
    font-size: 16px;
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
    }

    &:active {
        transform: translateY(0);
    }
}
</style>
